import { CreditCard, Download } from 'lucide-react'
import { Button } from '@components/ui/button'
import { Card, CardContent } from '@components/ui/card'
import { usePortalInvoices } from '@hooks/usePortal'
import { useClinicFormat } from '@context/ClinicTimezoneContext'
import { cn } from '@lib/utils'

const invoiceStatusLabels: Record<string, string> = {
  paid: 'Pagada',
  overdue: 'Vencida',
  partially_paid: 'Pago parcial',
  pending: 'Pendiente',
}

export function PortalBillingPage() {
  const { formatDate } = useClinicFormat()
  const { data: invoices, isLoading } = usePortalInvoices()

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800'
      case 'overdue':
        return 'bg-red-100 text-red-800'
      case 'partially_paid':
        return 'bg-amber-100 text-amber-800'
      default:
        return 'bg-blue-100 text-blue-800'
    }
  }

  const totalBalance = invoices?.reduce((sum, inv) => sum + (inv.balance_due || 0), 0) || 0

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Facturación y recibos</h1>
        <p className="text-muted-foreground mt-1">Consulta y gestiona tus recibos</p>
      </div>

      <Card className={totalBalance > 0 ? 'border-amber-200' : ''}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Saldo pendiente total</p>
              <p className="text-3xl font-bold">${totalBalance.toFixed(2)}</p>
            </div>
            <div className="h-12 w-12 rounded-lg bg-medical-100 flex items-center justify-center">
              <CreditCard className="h-6 w-6 text-medical-600" />
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="h-24 animate-pulse"></Card>
          ))}
        </div>
      ) : !invoices?.length ? (
        <Card className="p-8 text-center">
          <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Sin recibos</h3>
          <p className="text-muted-foreground">No tienes recibos en este momento.</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {invoices.map((invoice) => (
            <Card key={invoice.id}>
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">Recibo #{invoice.invoice_number}</h3>
                      <span
                        className={cn(
                          'px-2 py-0.5 rounded text-xs font-medium',
                          getStatusColor(invoice.status)
                        )}
                      >
                        {invoiceStatusLabels[invoice.status] ?? invoice.status}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Emitido: {formatDate(invoice.issue_date)} • Vence:{' '}
                      {formatDate(invoice.due_date)}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-semibold text-lg">${invoice.balance_due.toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">
                        de ${invoice.total_amount.toFixed(2)} totales
                      </p>
                    </div>
                    {invoice.is_payable && (
                      <Button variant="outline" size="sm">Pagar ahora</Button>
                    )}
                    <Button variant="ghost" size="icon">
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
